/** Forge Stats SDK wrapper. No-op when `VITE_FORGE_STATS_KEY` unset. */
import type { ErrorEventOptions, EventMeta, Logger } from '@codeserk/forge-stats';
import {
  captureGlobalErrors as forgeCaptureGlobalErrors,
  init as forgeInit,
  setMeta as forgeSetMeta,
  track as forgeTrack,
  trackError as forgeTrackError,
  trackView as forgeTrackView,
  updateMeta as forgeUpdateMeta,
} from '@codeserk/forge-stats';
import {
  type AnalyticsEvent,
  type AnalyticsPlatform,
  EVENT_SCHEMA_VERSION,
  sanitizeData,
  splitEventName,
} from './events';

export type { AnalyticsPlatform } from './events';

const FORGE_HOST = 'api-events.forge.codeserk.es';
const ANON_DEVICE_KEY = 'infiltre:analytics:anon';
const INSTALL_ID_KEY = 'infiltre:analytics:install';
const SESSION_PREFIX = 'sess';

const safeUuid = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const readPersistent = (key: string): string | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writePersistent = (key: string, value: string): void => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / sandboxed iframe — caller already keeps in-memory copy
  }
};

const ensurePersistentId = (key: string, prefix: string): string => {
  const existing = readPersistent(key);
  if (existing) return existing;
  const fresh = `${prefix}-${safeUuid()}`;
  writePersistent(key, fresh);
  return fresh;
};

let anonDeviceId = '';
let installId = '';
let sessionId = '';

const resolveBaseUrl = (platform: AnalyticsPlatform): string | undefined => {
  if (platform === 'discord') {
    if (typeof window === 'undefined') return undefined;
    return `${window.location.origin}/.proxy/forge-stats`;
  }
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_FORGE_STATS_BASE_URL || undefined;
};

const readSdkKey = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_FORGE_STATS_KEY ?? '';
};

const readAppVersion = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_APP_VERSION ?? '0.0.0';
};

const readEnvironment = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_DEPLOY_ENV ?? env?.MODE ?? 'dev';
};

const isDev = (): boolean => {
  const env = (import.meta as { env?: Record<string, unknown> }).env;
  return Boolean(env?.DEV);
};

let initialized = false;

const devLogger: Logger = {
  error: (...args) => {
    if (typeof console !== 'undefined') console.warn('[forge-stats]', ...args);
  },
};
const silentLogger: Logger = {
  error: () => {
    // analytics SDK errors must not surface to user in prod
  },
};

export const initAnalytics = (platform: AnalyticsPlatform): void => {
  if (initialized) return;
  const sdk = readSdkKey();
  if (!sdk) return;
  const baseUrl = resolveBaseUrl(platform);
  forgeInit({
    sdk,
    baseUrl: baseUrl ?? `https://${FORGE_HOST}`,
    logger: isDev() ? devLogger : silentLogger,
  });
  const appVersion = readAppVersion();
  anonDeviceId = ensurePersistentId(ANON_DEVICE_KEY, 'anon');
  installId = ensurePersistentId(INSTALL_ID_KEY, 'install');
  sessionId = `${SESSION_PREFIX}-${safeUuid()}`;
  forgeSetMeta({
    appName: 'infiltre',
    appVersionName: appVersion,
    userId: anonDeviceId,
    userData: {
      platform,
      identitySource: 'anon-device',
      installId,
      sessionId,
      schemaVersion: EVENT_SCHEMA_VERSION,
      environment: readEnvironment(),
    },
  });
  forgeCaptureGlobalErrors();
  initialized = true;
  const locale = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  forgeTrack({
    type: 'session',
    name: `opened_${platform}`,
    data: { coldStart: true, locale, appVersion },
  });
};

export const getAnonDeviceId = (): string => anonDeviceId;
export const getInstallId = (): string => installId;
export const getSessionId = (): string => sessionId;

export const isAnalyticsReady = (): boolean => initialized;

/** Fire-and-forget. No-op pre-init. */
export const trackEvent = (event: AnalyticsEvent, meta?: EventMeta): void => {
  if (!initialized) return;
  const { type, name } = splitEventName(event.name);
  forgeTrack({ type, name, data: sanitizeData(event.data) }, meta);
};

export const trackView = (path: string, meta?: EventMeta): void => {
  if (!initialized) return;
  forgeTrackView(path, meta);
};

export const trackError = (error: Error, options?: ErrorEventOptions, meta?: EventMeta): void => {
  if (!initialized) return;
  forgeTrackError(error, options, meta);
};

/** Identity merge — extras go to `userData` to avoid Forge reserved keys. */
export const updateAnalyticsMeta = (meta: {
  userId?: string | null;
  userData?: Record<string, string | number | boolean | null>;
}): void => {
  if (!initialized) return;
  const userData: Record<string, string | number | boolean> = {};
  if (meta.userData) {
    for (const [k, v] of Object.entries(meta.userData)) {
      if (v !== null && v !== undefined) userData[k] = v;
    }
  }
  // `null` reverts to anon device id (never overwrites with '' — Forge keeps literal).
  const userId =
    meta.userId === null ? anonDeviceId : meta.userId !== undefined ? meta.userId : undefined;
  const next: EventMeta = {
    ...(userId !== undefined ? { userId } : {}),
    ...(Object.keys(userData).length > 0 ? { userData } : {}),
  };
  forgeUpdateMeta(next);
};

export const __resetAnalyticsForTests = (): void => {
  initialized = false;
  anonDeviceId = '';
  installId = '';
  sessionId = '';
};
